import {
  Box,
  DollarInput,
  FormItem,
  Icon,
  IconBackground,
  Typography,
} from '@branch-messenger/willow-ui'
import { ReceiptDollar } from '@branch-messenger/willow-ui/icons'
import { yupResolver } from '@hookform/resolvers/yup'
import { Controller } from 'react-hook-form'
import { useForm } from 'react-hook-form'

import {
  AdvanceAmountSchema,
  createAdvanceAmountSchema,
} from '@/containers/onDemandPay/TakeAdvance/lib/schemas/advance-amount-schema'
import { useTakeAdvance } from '@/containers/onDemandPay/TakeAdvance/TakeAdvanceContext'
import { useAdvanceQuote, useBranchVerification } from '@/selectors/ewa'

import {
  AdvanceStepTitle,
  AmountDisclaimer,
  TakeAdvanceContentContainer,
  TakeAdvanceFooterButton,
} from '../../styles'
import { TakeAdvanceContent } from '../../TakeAdvanceContent'
import { TakeAdvanceFooter } from '../../TakeAdvanceFooter'
import { AdvanceAmountHeader } from './AdvanceAmountHeader'
import { AdvanceQuickAmounts } from './AdvanceQuickAmounts'

export const AdvanceAmountContent = () => {
  const {
    data: { minAdvanceAmount, maxAdvanceAmount, minAmountRaw, maxAmountRaw },
  } = useBranchVerification()

  const { takeAdvanceValues, nextStep, setTakeAdvanceValues } = useTakeAdvance()

  const {
    watch,
    control,
    handleSubmit,
    setValue,
    formState: { isValid, errors },
  } = useForm<AdvanceAmountSchema>({
    resolver: yupResolver(
      createAdvanceAmountSchema({
        minAmount: minAmountRaw,
        maxAmount: maxAmountRaw,
      })
    ),
    defaultValues: {
      advance_amount: takeAdvanceValues.advance_amount,
    },
  })

  const advanceAmount = watch('advance_amount') ?? 0
  const { data: advanceQuote } = useAdvanceQuote(advanceAmount)

  const isDisabled = !isValid

  const handleAdvanceAmountSubmit = (data: AdvanceAmountSchema) => {
    setTakeAdvanceValues({
      ...takeAdvanceValues,
      advance_amount: data.advance_amount,
    })
    nextStep()
  }

  return (
    <TakeAdvanceContentContainer
      as="form"
      onSubmit={handleSubmit(handleAdvanceAmountSubmit)}
    >
      <TakeAdvanceContent>
        <Box
          $display="flex"
          $direction="column"
          $align="initial"
          $justify="space-between"
          $gap={6}
          $flex={1}
        >
          <Box $display="flex" $direction="column" $align="initial" $gap={6}>
            <AdvanceStepTitle>{`You can advance up to ${maxAdvanceAmount}`}</AdvanceStepTitle>
            <Box $display="flex" $gap={2} $direction="column" $align="initial">
              <AdvanceAmountHeader minAdvanceAmount={minAdvanceAmount} />
              <FormItem error={errors.advance_amount?.message}>
                <Controller
                  name="advance_amount"
                  control={control}
                  render={({ field }) => (
                    <DollarInput {...field} placeholder="Enter amount" />
                  )}
                />
              </FormItem>
              <AdvanceQuickAmounts
                amount={+advanceAmount}
                setAmount={v =>
                  setValue('advance_amount', v, {
                    shouldValidate: true,
                  })
                }
                maxAmount={maxAmountRaw}
              />
            </Box>
          </Box>
          {advanceQuote && advanceAmount && (
            <AmountDisclaimer>
              <IconBackground>
                <Icon Icon={ReceiptDollar} size={6} />
              </IconBackground>
              <Box>
                <Typography $bold $size="sm">
                  {`You'll receive ${advanceQuote?.receivedAmount}`}
                </Typography>
                <Typography $size="xs">
                  {`A ${advanceQuote?.feeAmount} fee will be subtracted from your advance when using a debit card. This fee is based on the total amount you advance.`}
                </Typography>
              </Box>
            </AmountDisclaimer>
          )}
        </Box>
      </TakeAdvanceContent>
      <TakeAdvanceFooter>
        <TakeAdvanceFooterButton type="submit" disabled={isDisabled}>
          Continue
        </TakeAdvanceFooterButton>
      </TakeAdvanceFooter>
    </TakeAdvanceContentContainer>
  )
}
